// src/pages/platform/HealthPage.tsx
import React, { useEffect, useState } from 'react';
import { CheckCircle, AlertCircle, XCircle, RefreshCw } from 'lucide-react';
import { apiClient, getErrorMessage } from '@/lib/api';
import Button from '@/components/ui/Button';
import { PageHeader } from '@/components/ui/PageHeader';
import { Panel } from '@/components/ui/Panel';
import { Badge } from '@/components/ui/Badge';
import { Notice } from '@/components/ui/Notice';
import { SkeletonLines } from '@/components/ui/Skeleton';

interface ServiceHealth {
  healthy: boolean;
  name?: string;
  latency?: number;
  error?: string;
  // false when the service has no health_check_url configured at all:
  // `healthy: true` in that case just means "nothing to report as broken",
  // not that an upstream was actually probed.
  checked?: boolean;
}

interface HealthResponse {
  timestamp: string;
  services: Record<string, ServiceHealth>;
  platform: Record<string, ServiceHealth>;
}

const StatusIcon: React.FC<{ service: ServiceHealth }> = ({ service }) => {
  if (!service.checked) return <AlertCircle className="h-5 w-5 shrink-0 text-warning" aria-hidden="true" />;
  if (service.healthy) return <CheckCircle className="h-5 w-5 shrink-0 text-success" aria-hidden="true" />;
  return <XCircle className="h-5 w-5 shrink-0 text-error" aria-hidden="true" />;
};

const statusBadge = (svc: ServiceHealth) =>
  !svc.checked ? <Badge tone="warning">Not checked</Badge>
    : svc.healthy ? <Badge tone="success">Healthy</Badge>
      : <Badge tone="error">Down</Badge>;

const HealthRow: React.FC<{ id: string; svc: ServiceHealth }> = ({ id, svc }) => (
  <li className="flex items-center justify-between gap-4 px-5 py-3">
    <div className="flex min-w-0 items-center gap-3">
      <StatusIcon service={svc} />
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-foreground">{svc.name ?? id}</p>
        <p className="truncate text-xs text-muted-foreground">
          <span className="font-mono">{id}</span>{svc.checked === false && ' · no health check configured'}
        </p>
        {svc.error && <p className="mt-0.5 max-w-xs truncate text-xs text-error" title={svc.error}>{svc.error}</p>}
      </div>
    </div>
    <div className="flex shrink-0 items-center gap-3">
      <span className="hidden font-mono text-[13px] tabular-nums text-muted-foreground sm:inline">{typeof svc.latency === 'number' ? `${svc.latency}ms` : '-'}</span>
      {statusBadge(svc)}
    </div>
  </li>
);

const HealthPage: React.FC = () => {
  const [data, setData] = useState<HealthResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const checkAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get<HealthResponse>('/admin/health');
      setData(res.data);
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAll();
    const interval = setInterval(checkAll, 30_000);
    return () => clearInterval(interval);
  }, []);

  const platform = data ? Object.entries(data.platform ?? {}) : [];
  const services = data ? Object.entries(data.services) : [];

  const firstLoad = loading && !data;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Platform health"
        description={data ? `Last checked: ${new Date(data.timestamp).toLocaleTimeString()}. Checks again every 30 seconds.` : 'Checks every service the gateway depends on, every 30 seconds.'}
        actions={
          <Button variant="outline" size="sm" onClick={checkAll} disabled={loading} leftIcon={<RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />}>
            Refresh
          </Button>
        }
      />
      {error && <Notice tone="error">{error}</Notice>}

      <Panel title="Platform infrastructure" bodyClassName="px-0 pb-2">
        {firstLoad ? (
          <SkeletonLines count={3} className="px-5 pb-3" lineClassName="h-12" />
        ) : (
          <>
            {platform.length === 0 && !loading && !error && (
              <p className="px-5 pb-4 pt-2 text-sm text-muted-foreground">No platform dependencies configured.</p>
            )}
            <ul className="divide-y divide-border">
              {platform.map(([key, svc]) => (
                <HealthRow key={key} id={key} svc={svc} />
              ))}
            </ul>
          </>
        )}
      </Panel>

      <Panel title="API services" bodyClassName="px-0 pb-2">
        {firstLoad ? (
          <SkeletonLines count={4} className="px-5 pb-3" lineClassName="h-12" />
        ) : (
          <>
            {services.length === 0 && !loading && !error && (
              <p className="px-5 pb-4 pt-2 text-sm text-muted-foreground">No services configured.</p>
            )}
            <ul className="divide-y divide-border">
              {services.map(([key, svc]) => (
                <HealthRow key={key} id={key} svc={svc} />
              ))}
            </ul>
          </>
        )}
      </Panel>
    </div>
  );
};

export default HealthPage;
